import { Icon } from '@iconify/react';
import { useInternationalization } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { get, isEmpty, pick } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import EditIcon from 'src/assets/icons/EditIcon';
import NoItemIcon from 'src/assets/icons/NoItemIcon';
import { ProductImage } from 'src/components/ProductImage';
import Button from 'src/components/design-system/Button';
import NumberStepper from 'src/components/design-system/NumberStepper';
import useAuth from 'src/hooks/useAuth';
import AddToFavoritesDropdown, {
  FolderOption,
} from 'src/sections/catalog_products/AddToFavoritesDropdown';
import ProductAttributeIcons from 'src/sections/catalog_products/ProductAttributeIcons';
import { ProductDetails } from 'src/types/catalog/ProductDetails';
import { convertToDate } from 'src/utils/formatTime';
import { canEditOfflineSupplierProduct } from 'src/utils/offlineSupplierProductAccess';
import { getPriceCurrencyFormat } from 'src/utils/price';
import { useContextSelector } from 'use-context-selector';
import { ShoppingCartContext, ShoppingCartContextProps } from './ShoppingCartContext';

interface ShoppingCartProductInformationViewProps {
  productData: ProductDetails;
  quantity: number;
  catalogType?: number;
  onQuantityChange: (value: number) => void;
  onImageClick: () => void;
  onReadMoreClick: () => void;
  onAddToFavorites: (folderId: number) => Promise<void>;
  onEdit: () => void;
  onSwapItemClick: () => void;
  isLoading?: boolean;
  folderOptions: FolderOption[];
}

export default function ShoppingCartProductInformationView({
  productData,
  quantity,
  catalogType,
  onQuantityChange,
  onImageClick,
  onReadMoreClick,
  onAddToFavorites,
  onEdit,
  onSwapItemClick,
  isLoading = false,
  folderOptions,
}: ShoppingCartProductInformationViewProps) {
  const { t } = useTranslation();
  const intl = useInternationalization();
  const { companyId } = useAuth();
  const isSupplierUser = useContextSelector(
    ShoppingCartContext,
    (context: ShoppingCartContextProps) => context.isSupplierUser
  );
  const isExternalCatalog = catalogType === 2;
  const isOfflineCatalog = catalogType === 3;
  const canEditProduct =
    isOfflineCatalog && canEditOfflineSupplierProduct(companyId, productData.supplierCompanyId);

  const productAttributes = useMemo(
    () =>
      [
        productData.isContracted ? 'isContracted' : null,
        productData.productInFavoriteFolder ? 'productInFavoriteFolder' : null,
        productData.isImageAvailable ? 'isImageAvailable' : null,
        productData.isSpecialOrder ? 'isSpecialOrder' : null,
        productData.isOfflineSupplierItem ? 'isOfflineSupplierItem' : null,
        productData.isFormulary ? 'isFormulary' : null,
      ].filter((attribute): attribute is string => attribute !== null),
    [
      productData.isContracted,
      productData.productInFavoriteFolder,
      productData.isImageAvailable,
      productData.isSpecialOrder,
      productData.isOfflineSupplierItem,
      productData.isFormulary,
    ]
  );

  const keyMap: Record<string, string> = {
    manufacturerName: t('Mfg Name'),
    manufacturerNumber: t('Mfg #'),
    brandName: t('Brand Name'),
    model: t('Model'),
    distributionNum: t('Distribution #'),
    leadTime: t('Lead Time (Days)'),
    uom: t('UOM'),
    casePack: t('Case Pack Units'),
    packDimensions: t('Case Pack Dimensions'),
    weight: t('Weight'),
    lastPurchaseDate: t('Shopping List Last Purchased'),
    companyName: t('Supplier'),
    itemNum: t('Item #'),
    offerNum: t('Offer #'),
    offerDescription: t('Offer Description'),
    commodityCode: t('Commodity Code'),
    upc: t('UPC'),
    unspc: t('UNSPSC'),
    productType: t('Item Class'),
    minOrder: t('Minimum Order'),
    catalogName: t('Catalog'),
  };

  const manufacturingAndPackaging = useMemo(
    () =>
      pick(productData, [
        'manufacturerName',
        'manufacturerNumber',
        'brandName',
        'model',
        'distributionNum',
        'leadTime',
        'uom',
        'casePack',
        'packDimensions',
        'weight',
      ]),
    [productData]
  );

  const productDetails = useMemo(
    () =>
      pick(productData, [
        'companyName',
        'itemNum',
        'offerNum',
        'offerDescription',
        'commodityCode',
        'upc',
        'unspc',
        'productType',
        'lastPurchaseDate',
        'minOrder',
        'catalogName',
      ]),
    [productData]
  );

  const getValue = (key: string, v: string | number | null): string | number => {
    if (key === 'lastPurchaseDate') {
      return isEmpty(v) ? '' : convertToDate(v as string);
    }
    if (key === 'productType') {
      return (v as number | string).toString() === '0' ? t('Goods') : t('Services');
    }
    if (typeof v === 'string') {
      const trimmedValue = v.trim();
      return isEmpty(trimmedValue) ? '' : trimmedValue;
    }
    if (isEmpty(v)) {
      return '';
    }
    return v as string | number;
  };

  const getTooltipContent = (key: string, v: string | number | null): string | undefined => {
    const value = getValue(key, v);
    const stringValue = value.toString();
    return stringValue && stringValue.trim() !== '' ? stringValue : undefined;
  };

  const handleOpenItemUrl = useCallback(() => {
    const itemUrl = get(productData, 'linkUrl', '');
    if (itemUrl) {
      window.open(itemUrl, '_blank', 'noopener,noreferrer');
    }
  }, [productData]);

  const getProductInfo = () => {
    const productInfo: string = get(productData, 'info', '');
    if (productInfo && productInfo.length > 300) {
      return productInfo.substring(0, 300) + '...';
    }
    return productInfo;
  };

  const renderProductInfo = () => {
    const productInfo: string = get(productData, 'info', '');
    if (productInfo && productInfo.length > 300) {
      return (
        <div className="text-sm leading-relaxed text-gray-700 relative">
          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            className="break-words"
          >
            {getProductInfo()}
          </span>
          {productInfo.length > 300 && (
            <span
              onClick={onReadMoreClick}
              className="text-[#0E80B4] hover:underline cursor-pointer inline-block mt-1"
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('readMore')}
            </span>
          )}
        </div>
      );
    }
    return productInfo;
  };

  return (
    <>
      <div className="pb-8 mt-4">
        {/* Top Section - Product Image and Info */}
        <div className="grid grid-cols-1 lg:grid-cols-[4fr_6fr] gap-5 mb-8 min-w-0">
          {/* Left Column - Product Image (40% width) */}
          <div className="flex flex-col min-w-0">
            <div
              className={clsx(
                'rounded shadow-sm border border-gray-300 cursor-pointer flex items-center justify-center w-full bg-gray-200 overflow-hidden',
                {
                  'cursor-not-allowed': !productData.isImageAvailable,
                }
              )}
              onClick={() => {
                const childImage = document.getElementById(productData.productKey);
                if (childImage) {
                  const source: string | null = childImage.getAttribute('src');
                  if (!isEmpty(source)) {
                    if (source?.toLowerCase().includes('fallback')) {
                      return;
                    } else {
                      if (productData.isImageAvailable) {
                        if (
                          productData.imageDescription === 'View website' &&
                          productData.linkUrl
                        ) {
                          handleOpenItemUrl();
                          return;
                        }
                        onImageClick();
                      }
                    }
                  } else {
                    return;
                  }
                }
              }}
            >
              <ProductImage
                productKey={productData.productKey}
                productId={productData.productId}
                supplierId={productData.supplierId}
                linkUrl={productData.linkUrl}
                altText={productData.altText}
                containerHeight="300px"
                containerWidth="370px"
                imageHeight="300px"
                imageWidth="450px"
                type="detail"
              />
            </div>
          </div>

          {/* Right Column - Product Info (60% width, no box) */}
          <div className="flex flex-col min-w-0">
            <div className="flex-1">
              <div className="grid grid-rows-1 grid-cols-[1fr_auto] gap-2 mb-2 place-items-start">
                <h2
                  className="font-bold text-gray-800 break-words flex-1 min-w-0 w-[100%] line-clamp-2"
                  style={{ fontSize: '18px' }}
                  data-tooltip-id="product-information-tooltip"
                  data-tooltip-content={getTooltipContent('description', productData.description)}
                >
                  {getValue('description', productData.description)}
                </h2>
                <div className="pt-1 h-full flex items-center">
                  <ProductAttributeIcons attributes={productAttributes} />
                </div>
              </div>
              <p className="text-[#AAAAAA] text-md font-[400] line-clamp-2 break-all mb-4">
                {(() => {
                  const priceNumber = parseFloat(
                    get(productData, 'price', 0)
                      .toString()
                      .replace(/[^0-9.-]{1,}/gi, '')
                  );
                  const formattedPrice = intl.format(
                    getPriceCurrencyFormat(productData?.uom),
                    Number(priceNumber)
                  );
                  const uom = productData.uom || '';

                  return (
                    <>
                      <span className="text-[#0E80B4] text-lg font-bold">{formattedPrice}</span>
                      {uom && <span> / {getValue('uom', uom)}</span>}
                    </>
                  );
                })()}
              </p>
              <div className="mb-4">{renderProductInfo()}</div>
            </div>

            {/* Quantity and Action Buttons - Aligned with image bottom */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {!isExternalCatalog && !isSupplierUser ? (
                <div className="flex items-center gap-2 w-full">
                  <div className="flex items-center gap-2 w-[25%]">
                    <label className="text-sm font-medium text-gray-500">{t('Qty')}</label>
                    <NumberStepper
                      value={quantity}
                      onChange={(e, value) => onQuantityChange(value)}
                    />
                  </div>
                  {productData.isNoItem && (
                    <div
                      data-tooltip-id="product-information-tooltip"
                      data-tooltip-content={t('Check Contracted Alternatives')}
                      onClick={onSwapItemClick}
                      className="cursor-pointer"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          onSwapItemClick();
                        }
                      }}
                    >
                      <NoItemIcon />
                    </div>
                  )}
                  <AddToFavoritesDropdown
                    folders={folderOptions}
                    onAdd={onAddToFavorites}
                    isLoading={isLoading}
                  />
                  {canEditProduct && (
                    <Button color="info" onClick={onEdit} size="md">
                      <EditIcon size="16px" />
                      {t('Edit')}
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Bottom Section - Product Details and Manufacturing - No boxes/borders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-8">
          {/* Product Details */}
          <div className="flex flex-col w-full min-w-0">
            {/* Title */}
            <h3 className="text-xs uppercase text-gray-800 mb-3 ml-1" style={{ fontWeight: 400 }}>
              {t('Item Details')}
            </h3>
            {/* Table Content */}
            <div className="bg-white flex flex-col shadow-sm border border-gray-200 overflow-x-auto w-[100%]">
              {Object.entries(productDetails).map(([key, value], index) => (
                <div
                  key={key}
                  className={`px-3 py-2 flex flex-col sm:flex-row sm:justify-between border-b border-gray-200 gap-1 sm:gap-2 max-w-[100%] ${
                    index % 2 === 0 ? 'bg-white' : 'bg-[#ecf7fb]'
                  }`}
                >
                  <span
                    className={clsx(
                      'text-gray-500 flex-shrink-0 break-words',
                      key === 'lastPurchaseDate' ? 'max-w-[60%] min-w-0' : 'max-w-[35%] truncate'
                    )}
                    style={{ fontSize: '12px', fontWeight: 400 }}
                  >
                    {key === 'lastPurchaseDate' ? (
                      <span className="flex flex-row items-center min-w-0 w-full">
                        <span className="truncate mr-1" style={{ fontSize: '12px' }}>
                          {t('Shopping List Last Purchased')}
                        </span>
                        <Icon
                          icon="mdi:information-outline"
                          width="13"
                          height="13"
                          className="cursor-pointer shrink-0"
                          data-tooltip-id="product-information-tooltip"
                          data-tooltip-content={t('shoppingListLastPurchasedTooltip')}
                        />
                      </span>
                    ) : (
                      keyMap[key]
                    )}
                  </span>
                  <span
                    className="text-black sm:text-right break-words truncate max-w-[65%]"
                    style={{ fontSize: '12px', fontWeight: 500 }}
                    data-tooltip-id="product-information-tooltip"
                    data-tooltip-content={getTooltipContent(key, value)}
                  >
                    {getValue(key, value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Manufacturing and Packaging */}
          <div className="flex flex-col w-full min-w-0">
            {/* Title */}
            <h3 className="text-xs uppercase text-gray-800 mb-3 ml-1" style={{ fontWeight: 400 }}>
              {t('Manufacturing and Packaging')}
            </h3>
            {/* Table Content */}
            <div className="bg-white flex flex-col shadow-sm border border-gray-200 overflow-x-auto w-[100%]">
              {Object.entries(manufacturingAndPackaging).map(([key, value], index) => (
                <div
                  key={key}
                  className={`px-3 py-2 flex flex-col sm:flex-row sm:justify-between border-b border-gray-200 gap-1 sm:gap-2 max-w-[100%] ${
                    index % 2 === 0 ? 'bg-white' : 'bg-[#ecf7fb]'
                  }`}
                >
                  <span
                    className="text-gray-500 flex-shrink-0 break-words truncate max-w-[35%]"
                    style={{ fontSize: '12px', fontWeight: 400 }}
                  >
                    {keyMap[key]}
                  </span>
                  <span
                    className="text-black sm:text-right break-words truncate max-w-[65%]"
                    style={{ fontSize: '12px', fontWeight: 500 }}
                    data-tooltip-id="product-information-tooltip"
                    data-tooltip-content={getTooltipContent(key, value)}
                  >
                    {getValue(key, value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Tooltip
        id="product-information-tooltip"
        style={{
          backgroundColor: 'black',
          color: 'white',
          zIndex: 99999,
          maxWidth: '80%',
          wordWrap: 'break-word',
        }}
        place="bottom"
      />
    </>
  );
}
